import { RefObject, useEffect } from "react";
import { trazaAviso } from "../../../appMovil/appMovil";

const esIos = (): boolean => /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
/**
 * iOS 26 (video de Esteban, 2026-10-05): con el teclado abierto, la barra de dirección de Safari y la barra del ✓ FLOTAN
 * encima de la página, dentro de lo que el navegador reporta como visible (~100 pt juntas). La app instalada (iOS 26): la barra
 * del ✓ queda fuera de lo visible (medido 2026-10-10).
 */
// Medido en el iPhone de Esteban (2026-10-10, iOS 26, WAPP instalada): la barra del ✓ queda FUERA de lo visible, sin relleno.
const flotantesIos = (): number => ((navigator as any).standalone ? 0 : 104);
/** Contenedores que no deben desplazarse: al enfocar el cuadro el iPhone los recorre y la pantalla "se va para arriba". */
const FIJOS = ".crm, .crm-cuerpo, .ventas, .ventas-cuerpo, .ventas-cliente, .cv, .cv-cuerpo, .cv-centro, .cli-cab";

/**
 * Teléfono: CRM y WappChat miden lo que de verdad se ve (debajo de la barra de WAPP) y se ajustan a eso, así la caja de
 * escribir queda pegada abajo y la página no se desliza.
 * Teclado abierto: la pantalla se pega a lo visible (sin la barra de WAPP ni las pestañas), se regresan a cero los
 * desplazamientos que el iPhone mete al enfocar, y abajo se deja libre el espacio de las barras flotantes de iOS, en
 * blanco como la caja de escribir (así no queda tapada ni se ve un hueco gris).
 */
export const useAltoVisible = (ref: RefObject<HTMLElement>, cuando?: any) => {
	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		const vv = (window as any).visualViewport;
		const angosta = window.matchMedia("(max-width: 700px)");
		const raiz = document.documentElement;
		let fijo = false;
		const enderezar = () => {
			if (el.scrollTop) el.scrollTop = 0;
			el.querySelectorAll<HTMLElement>(FIJOS).forEach((x) => { if (x.scrollTop) x.scrollTop = 0; });
		};
		const soltar = () => {
			el.classList.remove("con-teclado");
			["position", "top", "left", "right", "zIndex", "boxSizing", "paddingBottom", "minHeight"].forEach((k) => { (el.style as any)[k] = ""; });
			if (fijo) { fijo = false; raiz.style.overflow = ""; document.body.style.overflow = ""; if (window.scrollY) window.scrollTo(0, 0); }
		};
		// Una sola pasada por cuadro de animación y solo si algo cambió de verdad: corregir en cada evento del iPhone
		// peleaba con su propio movimiento y la pantalla "temblaba" (Esteban, 2026-10-05).
		// Traza temporal (2026-10-10, Esteban: "se va aún más para arriba al poner enviar mensaje"): medidas reales del iPhone.
		const medir = (evento: string) => {
			try {
				if (!esIos()) return;
				const sonda = document.createElement("div");
				sonda.style.cssText = "position:fixed;visibility:hidden;padding:env(safe-area-inset-top) 0 env(safe-area-inset-bottom) 0";
				document.body.appendChild(sonda);
				const cs = getComputedStyle(sonda);
				const seguro = { arriba: cs.paddingTop, abajo: cs.paddingBottom };
				sonda.remove();
				const r = (q: string) => { const x = el.querySelector(q) as HTMLElement | null; if (!x) return null; const b = x.getBoundingClientRect(); return [Math.round(b.top), Math.round(b.bottom)]; };
				trazaAviso({ evento, altoBase, ruta: window.location.pathname, inner: [window.innerWidth, window.innerHeight], vv: vv ? [Math.round(vv.width), Math.round(vv.height), Math.round(vv.offsetTop), Math.round(vv.pageTop)] : null,
					scrollY: Math.round(window.scrollY), seguro, cont: r(":scope") || [Math.round(el.getBoundingClientRect().top), Math.round(el.getBoundingClientRect().bottom)],
					contPad: el.style.paddingBottom, compositor: r(".cv-compositor"), cab: r(".cli-cab"), topNav: (() => { const t = document.querySelector(".topNav"); if (!t) return null; const b = t.getBoundingClientRect(); return [Math.round(b.top), Math.round(b.bottom)]; })() });
			} catch { /* nada */ }
		};
		let medidoReposo = false;
		let altoBase = 0;
		let anchoBase = window.innerWidth;
		let cuadro = 0;
		let ultimo = "";
		const aplicar = () => {
			cuadro = 0;
			if (!angosta.matches) { soltar(); el.style.height = ""; ultimo = ""; return; }
			const visible = Math.round(vv ? vv.height : window.innerHeight);
			// Dos formas de abrir el teclado en el iPhone (traza y video de Esteban, 2026-10-10):
			// - Safari: la página no cambia de alto y el teclado la tapa (innerHeight > lo visible).
			// - WAPP instalada (iOS 26): TODA la página se encoge al alto visible (innerHeight = lo visible = 409) y el iPhone
			//   la desplaza para enseñar el cuadro. Ahí solo se nota contra el alto que tenía sin teclado.
			const enfocado = (() => { const a = document.activeElement as HTMLElement | null; return !!a && (a.tagName === "TEXTAREA" || (a.tagName === "INPUT" && !/^(checkbox|radio|button|submit|file)$/i.test((a as HTMLInputElement).type)) || a.isContentEditable); })();
			if (window.innerWidth !== anchoBase) { anchoBase = window.innerWidth; altoBase = 0; }   // giró el teléfono
			if (!enfocado) altoBase = Math.max(altoBase, window.innerHeight);
			const encogida = !!vv && enfocado && altoBase > 0 && altoBase - window.innerHeight > 120;
			const teclado = !!vv && (window.innerHeight - vv.height > 120 || encogida);
			if (teclado) {
				// Siempre donde el iPhone dice que empieza lo visible (offsetTop). Con WAPP instalada alterna entre la página en su
				// lugar (offsetTop 0) y la página recorrida (offsetTop ~403); regresarla con scrollTo peleaba con el iPhone y la
				// pantalla rebotaba (video de Esteban, 2026-10-10).
				const top = Math.round(vv.offsetTop);
				const firma = "t" + top + ":" + visible;
				if (firma === ultimo) return;
				const entrando = !fijo;
				ultimo = firma;
				fijo = true;
				if (entrando) {
					raiz.style.overflow = "hidden"; document.body.style.overflow = "hidden";
					el.classList.add("con-teclado");
				}
				Object.assign(el.style, {
					position: "fixed", top: top + "px", left: "0", right: "0", zIndex: "1040", boxSizing: "border-box",
					// minHeight 0: .crm trae min-height 480 y con el teclado solo se ven ~409 (la caja quedaba debajo de la barra del ✓).
					height: visible + "px", minHeight: "0", paddingBottom: (esIos() && !encogida ? flotantesIos() : 0) + "px",
				});
				if (entrando) { enderezar(); medir("teclado_abre"); setTimeout(() => medir("teclado_600ms"), 600); }
				return;
			}
			const estaba = fijo;
			soltar();
			const arriba = el.getBoundingClientRect().top + window.scrollY;
			const alto = Math.max(320, Math.floor(visible - arriba));
			const firma = "n" + alto;
			if (firma === ultimo && !estaba) return;
			ultimo = firma;
			el.style.height = alto + "px";
			if (!medidoReposo && el.querySelector(".cv-compositor")) { medidoReposo = true; setTimeout(() => medir("reposo"), 300); }
			if (estaba) enderezar();
		};
		const ajustar = () => { if (!cuadro) cuadro = requestAnimationFrame(aplicar); };
		let tFoco: any = null;
		const alEnfocar = () => { clearTimeout(tFoco); tFoco = setTimeout(ajustar, 350); };
		ajustar();
		const t = setTimeout(ajustar, 300);   // la barra de WAPP termina de pintarse después
		vv?.addEventListener("resize", ajustar);
		vv?.addEventListener("scroll", ajustar);
		window.addEventListener("resize", ajustar);
		el.addEventListener("focusin", alEnfocar);
		el.addEventListener("focusout", alEnfocar);
		angosta.addEventListener?.("change", ajustar);
		return () => {
			clearTimeout(t);
			clearTimeout(tFoco);
			cancelAnimationFrame(cuadro);
			vv?.removeEventListener("resize", ajustar);
			vv?.removeEventListener("scroll", ajustar);
			window.removeEventListener("resize", ajustar);
			el.removeEventListener("focusin", alEnfocar);
			el.removeEventListener("focusout", alEnfocar);
			angosta.removeEventListener?.("change", ajustar);
			soltar();
			el.style.height = "";
		};
	}, [cuando]);
};
